import { UseQueryResult, useQuery } from 'react-query';
import { getRpcError } from 'client/modules/common/utils/rpcError';
import { GetTeamAttributes } from '.';
import { MCP_ATTRIBUTES } from '../../constants/mcpAttributes';
import { getTeamAttribute } from '../api/GetTeamAttribute';

export type TeamAttributeValue = {
  attribute: string;
  value: string | null;
  type: string;
  updatedAt: string;
  updatedBy: string;
};

type Params = { id_team: number };
type Return = Array<TeamAttributeValue>;

const NOT_FOUND = 404;

const emptyValue = (attribute: string): TeamAttributeValue => ({
  attribute,
  value: null,
  type: '',
  updatedAt: '',
  updatedBy: '',
});

export const getMcpTeamAttributes = async ({
  id_team,
}: Params): Promise<Return> => {
  const results = await Promise.allSettled(
    MCP_ATTRIBUTES.map(({ attribute }) =>
      getTeamAttribute({ id_team, attribute })
    )
  );

  return results.map((result, index) => {
    const { attribute } = MCP_ATTRIBUTES[index];

    if (result.status === 'rejected') {
      if (getRpcError(result.reason).status === NOT_FOUND) {
        return emptyValue(attribute);
      }
      throw result.reason;
    }

    return {
      attribute,
      value: result.value?.value ?? null,
      type: result.value?.type ?? '',
      updatedAt: result.value?.updated_at ?? '',
      updatedBy: result.value?.updated_by ?? '',
    };
  });
};

export const useGetTeamAttributesQuery = ({
  enabled = false,
  params,
}: {
  enabled: boolean;
  params: Params;
}): UseQueryResult<Return, Error> =>
  useQuery<Return, Error, Return, [GetTeamAttributes, Params]>(
    ['GetTeamAttributes', params],
    () => getMcpTeamAttributes(params),
    {
      enabled,
      notifyOnChangeProps: ['data', 'isFetching', 'error'],
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }
  );
